import EventEmitter from 'events';
import { Client as CasablancaClient, RiverDbManager, UnauthenticatedClient, isGDMChannelStreamId, makeStreamRpcClient, userIdFromAddress, makeRiverRpcClient, transformAttachments, } from '@towns-protocol/sdk';
import { ChannelMessage_Post_MentionSchema, } from '@towns-protocol/proto';
import { TransactionStatus, logTxnResult, JoinFlowStatus, CreateSpaceFlowStatus, } from './TownsClientTypes';
import { toMembership, Membership, MessageType } from '@towns-protocol/sdk';
import { addCategoryToError, SignerUndefinedError } from '../types/error-types';
import { makeSpaceStreamId, makeDefaultChannelStreamId } from '@towns-protocol/sdk';
import { retryOperation, staticAssertNever } from '../utils/towns-utils';
import { toStreamView } from './casablanca/CasablancaUtils';
import { BlockchainTransactionType } from '../types/web3-types';
import { Permission } from '@towns-protocol/web3';
import { TimeTrackerEvents, getTimeTracker } from '../SequenceTimeTracker';
import { BaseTransactor } from './BaseTransactor';
import { getSpaceReviewEventData } from '@towns-protocol/web3';
import { create } from '@bufbuild/protobuf';
export class TownsClient extends EventEmitter {
    opts;
    name;
    spaceDapp;
    casablancaClient;
    _signerContext;
    _eventHandlers;
    supportedXChainIds;
    analytics;
    createLegacySpaces;
    baseTransactor;
    constructor(opts, spaceDapp, name) {
        super();
        this.opts = opts;
        this.createLegacySpaces = opts.createLegacySpaces === true;
        this.analytics = opts.analytics;
        this.name = name || Math.random().toString(36).substring(7);
        // Don't log the analytics object, it's large and not useful
        const { analytics: _, ...rest } = this.opts;
        console.log('~~~ new TownsClient ~~~', this.name, rest);
        this.spaceDapp = spaceDapp;
        // this.blockchainTransactionStore = new BlockchainTransactionStore(this.spaceDapp)
        this._eventHandlers = opts.eventHandlers;
        this.baseTransactor = new BaseTransactor({
            accountAbstractionConfig: opts.accountAbstractionConfig,
            baseProvider: opts.baseProvider,
            baseConfig: opts.baseConfig,
            spaceDapp: this.spaceDapp,
            analytics: this.analytics,
            createLegacySpaces: this.createLegacySpaces,
            xchainConfig: this.opts.xchainConfig,
        });
    }
    get signerContext() {
        return this._signerContext;
    }
    isAccountAbstractionEnabled() {
        return !!this.opts.accountAbstractionConfig?.aaRpcUrl;
    }
    getAbstractAccountAddress({ rootKeyAddress }) {
        try {
            return this.baseTransactor.userOps?.getAbstractAccountAddress({ rootKeyAddress });
        }
        catch (error) {
            this.log('[getAbstractAccountAddress]', error);
        }
    }
    /************************************************
     * getServerVersions
     *************************************************/
    // eslint-disable-next-line @typescript-eslint/require-await
    async getServerVersions() {
        // TODO casablanca, return server versions
        return {
            versions: [],
            unstable_features: {},
            release_version: '0.0.0',
        };
    }
    /************************************************
     * logout
     *************************************************/
    async logout() {
        this.log('logout');
        await this.logoutFromCasablanca();
        await this.baseTransactor.blockchainTransactionStore.stop();
        await this.baseTransactor.userOps?.reset();
    }
    /************************************************
     * logoutFromCasablanca
     *************************************************/
    async logoutFromCasablanca() {
        if (!this._signerContext) {
            return;
        }
        this.log('logoutFromCasablanca');
        await this.stopCasablancaClient();
        this._eventHandlers?.onLogout?.({
            userId: this._signerContext?.creatorAddress.toString() ?? 'unset',
        });
        this._signerContext = undefined;
    }
    async makeUnauthenticatedClient() {
        if (!this.opts.riverProvider) {
            throw new Error('riverChainProvider is required');
        }
        const rpcClient = await makeRiverRpcClient(this.opts.riverProvider, this.opts.riverConfig);
        return new UnauthenticatedClient(rpcClient);
    }
    /************************************************
     * localStartCasablancaClientWithRetries
     * for use in joining or creating a space for a new user
     * a few things can happen:
     * - the network is down
     * - the isEntitled call can race during space creation and fail even though the space was created (alchemy isn't up to date on the node)
     *************************************************/
    async localStartCasablancaClientWithRetries(context, metadata, sequenceName) {
        const maxRetries = 3;
        let retryCount = 0;
        const getRetryDelay = (retryCount) => {
            return Math.min(1000 * 2 ** retryCount, 20000); // 2, 4, 8 seconds if max retries is 3
        };
        // eslint-disable-next-line no-constant-condition
        while (true) {
            try {
                return await this.startCasablancaClient(context, metadata, sequenceName);
            }
            catch (e) {
                retryCount++;
                console.error('[localStartCasablancaClientWithRetries] error starting casablanca client', { error: e, retryCount });
                await this.logoutFromCasablanca();
                if (retryCount > maxRetries) {
                    throw e;
                }
                const retryDelay = getRetryDelay(retryCount);
                console.log('[localStartCasablancaClientWithRetries] retrying', {
                    retryDelay,
                    retryCount,
                });
                await new Promise((resolve) => setTimeout(resolve, retryDelay));
            }
        }
    }
    /************************************************
     * startCasablancaClient
     *************************************************/
    async startCasablancaClient(context, metadata, sequenceName) {
        this.log('startCasablancaClient', context);
        if (this.casablancaClient) {
            throw new Error('already started casablancaClient');
        }
        if (!this.opts.riverProvider) {
            throw new Error('riverChainProvider is required');
        }
        this._signerContext = context;
        let rpcClient;
        let endMakeRiverRpc;
        if (sequenceName) {
            endMakeRiverRpc = getTimeTracker().startMeasurement(sequenceName, 'river_make_rpc_client');
        }
        // to force a specific rpc url, open the console and type `localStorage.RIVER_RPC_URL = 'https://river1.nodes.gamma.towns.com'`
        if (localStorage.getItem('RIVER_RPC_URL')) {
            rpcClient = makeStreamRpcClient(localStorage.getItem('RIVER_RPC_URL'));
        }
        else {
            rpcClient = await makeRiverRpcClient(this.opts.riverProvider, this.opts.riverConfig);
        }
        if (endMakeRiverRpc) {
            endMakeRiverRpc();
        }
        const userId = userIdFromAddress(context.creatorAddress);
        const cryptoDbName = this.cryptoDbName(context);
        const persistenceDbName = this.persistenceDbName(context);
        const cryptoStore = RiverDbManager.getCryptoDb(userId, cryptoDbName);
        this.casablancaClient = new CasablancaClient(context, rpcClient, cryptoStore, this, {
            persistenceStoreName: persistenceDbName,
            logNamespaceFilter: this.opts.logNamespaceFilter,
            highPriorityStreamIds: this.opts.highPriorityStreamIds,
            unpackEnvelopeOpts: this.opts.unpackEnvelopeOpts,
            streamOpts: { useModifySync: this.opts.useModifySync },
        });
        this.casablancaClient.setMaxListeners(100);
        let endIntializeUser;
        if (sequenceName) {
            endIntializeUser = getTimeTracker().startMeasurement(sequenceName, 'river_initialize_user');
        }
        const initTimes = await this.casablancaClient.initializeUser(metadata);
        if (sequenceName) {
            Object.entries(initTimes).forEach(([key, ms]) => {
                // init_crypto_time,
                // init_mls_time,
                // init_user_stream_time,
                // init_user_inbox_stream_time,
                // init_user_metadata_stream_time,
                // init_user_metadata_stream_init_from_persistence_time,
                // init_user_metadata_stream_get_user_stream_time,
                // init_user_metadata_stream_create_user_metadata_stream_time,
                // init_user_metadata_stream_init_from_response_time,
                // init_user_settings_stream_time,
                const snakeCaseKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
                getTimeTracker().addMeasurement(sequenceName, `river_${snakeCaseKey}`, ms);
            });
        }
        if (endIntializeUser) {
            endIntializeUser();
        }
        this._eventHandlers?.onRegister?.({
            userId: this.casablancaClient.userId,
        });
        this.log('xChainRpcUrls', this.opts.xchainConfig.supportedRpcUrls);
        this.casablancaClient.startSync();
        this.emit('onCasablancaClientCreated', this.casablancaClient);
        return this.casablancaClient;
    }
    persistenceDbName(signerContext) {
        const userId = userIdFromAddress(signerContext.creatorAddress);
        const envSuffix = this.opts.environmentId === 'gamma' ? '' : `-${this.opts.environmentId}`;
        const persistenceDbName = `persistence-${userId}${envSuffix}`;
        return persistenceDbName;
    }
    cryptoDbName(signerContext) {
        const userId = userIdFromAddress(signerContext.creatorAddress);
        const envSuffix = this.opts.environmentId === 'gamma' ? '' : `-${this.opts.environmentId}`;
        const cryptoDbName = `database-${userId}${envSuffix}`;
        return cryptoDbName;
    }
    /************************************************
     * stopCasablancaClient
     *************************************************/
    async stopCasablancaClient() {
        if (this.casablancaClient) {
            this.log('Stopped casablanca client');
            await this.casablancaClient.stop();
            this.casablancaClient = undefined;
        }
    }
    async createSpaceTransaction(createSpaceInfo, membership, signer, onSpaceCreateFlowStatus) {
        return this.baseTransactor.createSpaceTransaction(createSpaceInfo, membership, signer, onSpaceCreateFlowStatus);
    }
    async waitForCreateSpaceTransaction(context, signerContext, defaultUsernames = [], onCreateFlowStatus) {
        const txContext = await this.baseTransactor.waitForBlockchainTransaction(context, TimeTrackerEvents.CREATE_SPACE);
        if (txContext.status === TransactionStatus.Success && txContext.data) {
            this.log('[waitForCreateSpaceTransaction] space created on chain', txContext.data);
            try {
                onCreateFlowStatus?.(CreateSpaceFlowStatus.CreatingSpace);
                const spaceAddress = this.spaceDapp.getSpaceAddress(txContext.receipt, txContext.data.senderAddress);
                if (!spaceAddress) {
                    throw new Error('Space address not found');
                }
                const spaceId = makeSpaceStreamId(spaceAddress);
                const channelId = makeDefaultChannelStreamId(spaceAddress);
                txContext.data.spaceId = spaceId;
                txContext.data.channelId = channelId;
                const timeTracker = getTimeTracker();
                // wait until the space and channel are minted on-chain
                // before creating the streams
                if (!this.casablancaClient && signerContext) {
                    await this.localStartCasablancaClientWithRetries(signerContext, { spaceId }, TimeTrackerEvents.CREATE_SPACE);
                }
                if (!this.casablancaClient) {
                    throw new Error('casablancaClient not started');
                }
                const endCsbCreateSpace = timeTracker.startMeasurement(TimeTrackerEvents.CREATE_SPACE, 'river_create_space');
                const result = await this.casablancaClient.createSpace(spaceId);
                endCsbCreateSpace?.();
                const endCsbWaitForStream = timeTracker.startMeasurement(TimeTrackerEvents.CREATE_SPACE, 'river_wait_for_stream');
                onCreateFlowStatus?.(CreateSpaceFlowStatus.CreatingChannel);
                await this.casablancaClient.waitForStream(spaceId);
                this.log('[waitForCreateSpaceTransaction] Space stream created', {
                    result: result,
                    spaceId,
                });
                endCsbWaitForStream?.();
                if (defaultUsernames.length > 0) {
                    const endCsbSetUsername = timeTracker.startMeasurement(TimeTrackerEvents.CREATE_SPACE, 'river_set_username');
                    onCreateFlowStatus?.(CreateSpaceFlowStatus.CreatingUser);
                    // new space, no member, we can just set first username as default
                    await this.casablancaClient.setUsername(spaceId, defaultUsernames[0]);
                    endCsbSetUsername?.();
                    this.log('[waitForCreateSpaceTransaction] Set default username', {
                        defaultUsername: defaultUsernames[0],
                        spaceId,
                    });
                }
                const endCsbCreateDefaultChannel = timeTracker.startMeasurement(TimeTrackerEvents.CREATE_SPACE, 'river_create_default_channel');
                await this.createSpaceDefaultChannelRoom(spaceId, 'general', channelId);
                endCsbCreateDefaultChannel?.();
                this.log('[waitForCreateSpaceTransaction] default channel stream created', channelId);
                // emiting the event here, because the web app calls different
                // functions to create a space, and this is the only place
                // that all different functions go through
                this._eventHandlers?.onCreateSpace?.(spaceId);
            }
            catch (error) {
                addCategoryToError(error, 'river');
                throw error;
            }
        }
        logTxnResult('waitForCreateSpaceTransaction', txContext);
        return txContext;
    }
    /************************************************
     * createChannelWithId
     * Should only be used in cases where the channel is already created on-chain
     *************************************************/
    async createChannelWithId(createInfo, networkId) {
        if (!this.casablancaClient) {
            throw new Error("createChannel: Casablanca client doesn't exist");
        }
        if (networkId === undefined) {
            throw new Error('createChannel: networkId is undefined');
        }
        const { streamId } = await this.casablancaClient.createChannel(createInfo.parentSpaceId, createInfo.name, createInfo.topic ? createInfo.topic : '', networkId, createInfo.streamSettings, createInfo.channelSettings);
        await this.casablancaClient.waitForStream(streamId);
        return streamId;
    }
    async createSpaceDefaultChannelRoom(parentSpaceId, channelName, channelId) {
        const channelInfo = {
            name: channelName ?? 'general',
            parentSpaceId,
            roles: [],
        };
        return await this.createChannelWithId(channelInfo, channelId);
    }
    /************************************************
     * createChannel
     *************************************************/
    async createChannel(createChannelInfo, signer) {
        if (!signer) {
            throw new SignerUndefinedError();
        }
        this.log('[createChannel] creating channel', createChannelInfo);
        const txContext = await this.createChannelTransaction(createChannelInfo, signer);
        if (txContext.error) {
            throw txContext.error;
        }
        if (txContext.status === TransactionStatus.Pending) {
            const rxContext = await this.waitForCreateChannelTransaction(createChannelInfo, txContext);
            if (!rxContext) {
                throw new Error('no rxContext returned');
            }
            if (!rxContext.data) {
                throw new Error('no room identifier returned');
            }
            return rxContext.data;
        }
        // Something went wrong. Don't return a room identifier.
        throw new Error('Failed to create channel');
    }
    async createChannelTransaction(createChannelInfo, signer) {
        return this.baseTransactor.createChannelTransaction(createChannelInfo, signer);
    }
    async waitForCreateChannelTransaction(createChannelInfo, context) {
        const txnContext = await this.baseTransactor.waitForBlockchainTransaction(context);
        let riverError = undefined;
        if (txnContext.status === TransactionStatus.Success) {
            if (txnContext?.data) {
                const roomId = txnContext.data;
                // wait until the channel is minted on-chain
                // before creating the stream
                try {
                    await this.createChannelWithId(createChannelInfo, roomId);
                }
                catch (error) {
                    console.error('[waitForCreateChannelTransaction] river error', error);
                    riverError = error;
                }
                this.log('[waitForCreateChannelTransaction] Channel stream created', roomId);
            }
        }
        if (riverError) {
            txnContext.error = riverError;
            txnContext.status = TransactionStatus.Failed;
        }
        logTxnResult('waitForCreateChannelTransaction', txnContext);
        return txnContext;
    }
    async updateChannelTransaction(updateChannelInfo, signer, transactionType = BlockchainTransactionType.EditChannel) {
        return this.baseTransactor.updateChannelTransaction(updateChannelInfo, signer, transactionType);
    }
    async waitForUpdateChannelTransaction(context) {
        const txContext = await this.baseTransactor.waitForBlockchainTransaction(context);
        if (txContext.status === TransactionStatus.Success && txContext.data) {
            await this.updateChannelRoom(txContext.data);
        }
        logTxnResult('waitForUpdateChannelTransaction', txContext);
        return txContext;
    }
    async updateChannelRoom(updateChannelInfo) {
        if (!this.casablancaClient) {
            throw new Error("updatedChannel: Casablanca client doesn't exist");
        }
        if (updateChannelInfo.channelId === undefined) {
            throw new Error('updateChannel: channelId is undefined');
        }
        // update to updated info if it's defined, otherwise update to the current info
        await this.casablancaClient.updateChannel(updateChannelInfo.parentSpaceId, updateChannelInfo.channelId, '', '');
    }
    /************************************************
     * DMs
     *************************************************/
    async createDMChannel(userId) {
        const client = this.casablancaClient;
        if (!client) {
            throw new Error('No casablanca client');
        }
        const { streamId } = await client.createDMChannel(userId);
        return streamId;
    }
    /************************************************
     * GDMs
     *************************************************/
    async createGDMChannel(userIds) {
        const client = this.casablancaClient;
        if (!client) {
            throw new Error('No casablanca client');
        }
        const { streamId } = await client.createGDMChannel(userIds);
        return streamId;
    }
    /************************************************
     * Media
     *************************************************/
    async createMediaStream(channelId, spaceId, userId, chunkCount, firstChunk, firstChunkIv, perChunkEncryption) {
        if (!this.casablancaClient) {
            throw new Error("Casablanca client doesn't exist");
        }
        return await this.casablancaClient.createMediaStream(channelId, spaceId, userId, chunkCount, firstChunk, firstChunkIv, undefined, perChunkEncryption);
    }
    /************************************************
     * isOwner
     *************************************************/
    async isOwner(spaceId, user) {
        const spaceInfo = await this.spaceDapp.getSpaceInfo(spaceId);
        if (spaceInfo) {
            if (spaceInfo.owner === user) {
                return true;
            }
            else {
                const wallets = await this.getLinkedWalletsWithDelegations(user);
                return wallets.includes(spaceInfo.owner);
            }
        }
        return false;
    }
    /************************************************
     * isEntitled
     *************************************************/
    async isEntitled(spaceId, channelId, user, permission) {
        let isEntitled = false;
        if (channelId && spaceId) {
            isEntitled = await this.spaceDapp.isEntitledToChannel(spaceId, channelId, user, permission, this.opts.xchainConfig);
        }
        else if (spaceId) {
            if (permission === Permission.JoinSpace) {
                return await this.isEntitledToJoinSpace(spaceId, user);
            }
            isEntitled = await this.spaceDapp.isEntitledToSpace(spaceId, user, permission);
        }
        else {
            // TODO: Implement entitlement checks for DMs (channels without a space)
            // https://linear.app/hnt-labs/issue/HNT-3112/implement-entitlement-checks
            isEntitled = true;
        }
        // this.debug(
        //     `[isEntitled] is user entitled for channel and space for permission`,
        //     isEntitled,
        //     {
        //         user,
        //         spaceId: spaceId,
        //         channelId: channelId,
        //         permission: permission,
        //     },
        // )
        return isEntitled;
    }
    async isEntitledToJoinSpace(spaceId, rootKey) {
        if (!spaceId) {
            throw new Error('spaceId is required for permission JoinSpace');
        }
        const entitledWallet = await this.spaceDapp.getEntitledWalletForJoiningSpace(spaceId, rootKey, this.opts.xchainConfig);
        this.log(`[isEntitledToJoinSpace] is user entitlted for Permission.JoinSpace`, {
            entitledWallet,
            isEntitled: !!entitledWallet,
            spaceId: spaceId,
        });
        return !!entitledWallet;
    }
    /************************************************
     * getSpaceInfoBySpaceId
     *************************************************/
    async getSpaceInfoBySpaceId(spaceNetworkId) {
        return this.spaceDapp.getSpaceInfo(spaceNetworkId);
    }
    async createRoleTransaction(spaceNetworkId, roleName, permissions, users, ruleData, signer) {
        return this.baseTransactor.createRoleTransaction(spaceNetworkId, roleName, permissions, users, ruleData, signer);
    }
    async waitForCreateRoleTransaction(context) {
        const txResult = await this.baseTransactor.waitForBlockchainTransaction(context);
        if (txResult.status === TransactionStatus.Success && txResult.data?.spaceNetworkId) {
            const parsedLogs = await this.spaceDapp.parseSpaceLogs(txResult.data.spaceNetworkId, txResult.receipt.logs);
            const roleCreatedEvent = parsedLogs.find((log) => log?.name === 'RoleCreated');
            if (!roleCreatedEvent) {
                throw new Error('RoleCreated event not found');
            }
            const roleId = roleCreatedEvent.args[1].toNumber();
            const roleIdentifier = {
                roleId,
                spaceNetworkId: txResult.data.spaceNetworkId,
            };
            txResult.data = {
                spaceNetworkId: txResult.data.spaceNetworkId,
                roleId: roleIdentifier,
            };
        }
        logTxnResult('waitForCreateRoleTransaction', txResult);
        return txResult;
    }
    async addRoleToChannelTransaction(spaceNetworkId, channelNetworkId, roleId, signer) {
        return this.baseTransactor.addRoleToChannelTransaction(spaceNetworkId, channelNetworkId, roleId, signer);
    }
    async updateSpaceInfoTransaction(spaceNetworkId, name, uri, shortDescription, longDescription, signer) {
        return this.baseTransactor.updateSpaceInfoTransaction(spaceNetworkId, name, uri, shortDescription, longDescription, signer);
    }
    async refreshMetadataTransaction(spaceNetworkId, signer) {
        return this.baseTransactor.refreshMetadataTransaction(spaceNetworkId, signer);
    }
    async editSpaceMembershipTransaction(args) {
        return this.baseTransactor.editSpaceMembershipTransaction(args);
    }
    async waitForEditSpaceMembershipTransaction(context) {
        const txnContext = await this.baseTransactor.waitForBlockchainTransaction(context);
        logTxnResult('waitForEditSpaceMembershipTransaction', txnContext);
        return txnContext;
    }
    async updateRoleTransaction(spaceNetworkId, roleId, roleName, permissions, users, ruleData, signer) {
        return this.baseTransactor.updateRoleTransaction(spaceNetworkId, roleId, roleName, permissions, users, ruleData, signer);
    }
    async setChannelPermissionOverridesTransaction(spaceNetworkId, channelId, roleId, permissions, signer) {
        return this.baseTransactor.setChannelPermissionOverridesTransaction(spaceNetworkId, channelId, roleId, permissions, signer);
    }
    async clearChannelPermissionOverridesTransaction(spaceNetworkId, channelId, roleId, signer) {
        return this.baseTransactor.clearChannelPermissionOverridesTransaction(spaceNetworkId, channelId, roleId, signer);
    }
    async waitForAddRoleToChannelTransaction(context) {
        const txnContext = await this.baseTransactor.waitForBlockchainTransaction(context);
        logTxnResult('waitForAddRoleToChannelTransaction', txnContext);
        return txnContext;
    }
    async waitForUpdateSpaceInfoTransaction(context) {
        const txnContext = await this.baseTransactor.waitForBlockchainTransaction(context);
        logTxnResult('waitForUpdateSpaceInfoTransaction', txnContext);
        return txnContext;
    }
    async waitForUpdateRoleTransaction(context) {
        const txnContext = await this.baseTransactor.waitForBlockchainTransaction(context);
        logTxnResult('waitForUpdateRoleTransaction', txnContext);
        return txnContext;
    }
    async waitForSetChannelPermissionOverridesTransaction(context) {
        const txnContext = await this.baseTransactor.waitForBlockchainTransaction(context);
        logTxnResult('waitForSetChannelPermissionOverridesTransaction', txnContext);
        return txnContext;
    }
    async waitForRefreshMetadataTransaction(context) {
        const txnContext = await this.baseTransactor.waitForBlockchainTransaction(context);
        logTxnResult('waitForRefreshMetadataTransaction', txnContext);
        return txnContext;
    }
    async banTransaction(spaceId, walletAddress, signer) {
        return this.baseTransactor.banTransaction(spaceId, walletAddress, signer);
    }
    async unbanTransaction(spaceId, walletAddress, signer) {
        return this.baseTransactor.unbanTransaction(spaceId, walletAddress, signer);
    }
    async waitForBanUnbanTransaction(transactionContext) {
        const txnContext = await this.baseTransactor.waitForBlockchainTransaction(transactionContext);
        if (txnContext.status === TransactionStatus.Success && transactionContext.data?.isBan) {
            try {
                // Remove user from space if ban was successful
                await this.removeUser(transactionContext.data.spaceId, transactionContext.data.userId);
            }
            catch (error) {
                console.warn('[waitForBanTransaction] failed to remove user from River streams', error);
            }
        }
        logTxnResult('waitForBanTransaction', txnContext);
        return txnContext;
    }
    async walletAddressIsBanned(spaceId, walletAddress) {
        const wallets = (await this.getLinkedWalletsWithDelegations(walletAddress)).concat(walletAddress);
        const promises = wallets.map((walletAddress) => {
            return this.spaceDapp.walletAddressIsBanned(spaceId, walletAddress).then((result) => 
            // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
            result === true ? Promise.resolve(true) : Promise.reject(false));
        });
        try {
            await Promise.any(promises);
            return true;
        }
        catch {
            return false;
        }
    }
    async deleteRoleTransaction(spaceNetworkId, roleId, signer) {
        return this.baseTransactor.deleteRoleTransaction(spaceNetworkId, roleId, signer);
    }
    async waitForDeleteRoleTransaction(context) {
        const txnContext = await this.baseTransactor.waitForBlockchainTransaction(context);
        logTxnResult('waitForDeleteRoleTransaction', txnContext);
        return txnContext;
    }
    /************************************************
     * setSpaceAccess
     *************************************************/
    async setSpaceAccess(spaceNetworkId, disabled, signer) {
        return this.baseTransactor.setSpaceAccess(spaceNetworkId, disabled, signer);
    }
    /************************************************
     * inviteUser
     * if it's GDM, invite and auto join the user
     *************************************************/
    async inviteUser(roomId, userId) {
        if (!this.casablancaClient) {
            throw new Error('Casablanca client not initialized');
        }
        if (isGDMChannelStreamId(roomId)) {
            await this.casablancaClient.joinUser(roomId, userId);
        }
        else {
            await this.casablancaClient.inviteUser(roomId, userId);
        }
    }
    /************************************************
     * leave
     * ************************************************/
    async leave(roomId, _parentNetworkId) {
        if (!this.casablancaClient) {
            throw new Error('Casablanca client not initialized');
        }
        await this.casablancaClient.leaveStream(roomId);
    }
    /************************************************
     * joinRoom
     * - this function can handle joining both spaces and channels BUT should be used for channels only
     * - for spaces, use joinTown
     * @todo deprecate this in favor of separate joinTown and joinChannel functions
     *************************************************/
    async joinRoom(roomId, _parentNetworkId, opts) {
        // TODO: not doing event handlers here since Casablanca is not part of alpha
        if (!this.casablancaClient) {
            throw new Error('Casablanca client not initialized');
        }
        const stream = await this.casablancaClient.joinStream(roomId, opts);
        let parentId = roomId;
        if (stream.view.contentKind === 'channelContent' && stream.view.channelContent.spaceId) {
            parentId = stream.view.channelContent.spaceId;
        }
        this._eventHandlers?.onJoinRoom?.(roomId, parentId);
        return toStreamView(stream, this.getMembership(roomId));
    }
    async removeUser(streamId, userId) {
        if (!this.casablancaClient) {
            throw new Error('Casablanca client not initialized');
        }
        await this.casablancaClient.removeUser(streamId, userId);
    }
    async joinRiverSpaceAndDefaultChannels(spaceId, signerContext, onJoinFlowStatus, defaultUsername) {
        if (!this.casablancaClient && signerContext) {
            await this.localStartCasablancaClientWithRetries(signerContext, { spaceId }, TimeTrackerEvents.JOIN_SPACE);
        }
        onJoinFlowStatus?.(JoinFlowStatus.JoiningRoom);
        const endJoinSpace = getTimeTracker().startMeasurement(TimeTrackerEvents.JOIN_SPACE, 'river_joinroom_space');
        const room = await this.joinRoom(spaceId, undefined, {
            skipWaitForMiniblockConfirmation: true,
            skipWaitForUserStreamUpdate: true,
        });
        endJoinSpace?.();
        this.log('[joinTown] room', room);
        // join the default channels
        const spaceContent = this.casablancaClient?.streams.get(spaceId)?.view.spaceContent;
        if (spaceContent) {
            // if a default username is provided we can try applying it
            const defaultUsernamePromise = (async () => {
                if (!defaultUsername) {
                    this.log('[joinTown] no default username provided');
                    return;
                }
                const isAvailable = await this.getIsUsernameAvailable(spaceId, defaultUsername);
                if (!isAvailable) {
                    this.log('[joinTown] default usename already taken');
                    return;
                }
                try {
                    this.log('[joinTown] setting default username');
                    await this.casablancaClient?.setUsername(spaceId, defaultUsername);
                    this.log('[joinTown] default usename set');
                }
                catch (e) {
                    console.error('[joinTown] fail to set default username', e);
                }
            })();
            await Promise.all([
                defaultUsernamePromise,
                ...Array.from(spaceContent.spaceChannelsMetadata.entries())
                    .filter(([_, value]) => value.isDefault || value.isAutojoin)
                    .map(async ([key]) => {
                    onJoinFlowStatus?.(JoinFlowStatus.JoiningAutojoinChannels);
                    const endJoinChannel = getTimeTracker().startMeasurement(TimeTrackerEvents.JOIN_SPACE, 'river_joinroom_channel');
                    try {
                        await this.joinRoom(key, undefined, {
                            skipWaitForMiniblockConfirmation: true,
                            skipWaitForUserStreamUpdate: true,
                        });
                    }
                    catch (e) {
                        console.error('[joinTown] Failed auto-joining channel', e);
                    }
                    endJoinChannel?.();
                }),
            ]);
        }
        else {
            this.log('[joinTown] Error no space content found');
        }
        return room;
    }
    /************************************************
     * joinTown
     * - mints membership if needed
     * - joins the space
     *************************************************/
    async joinTown(spaceId, signer, signerContext, onJoinFlowStatus, defaultUsername) {
        if (!this.casablancaClient && !signerContext) {
            throw new Error('Casablanca client not initialized, pass signer context');
        }
        const userId = await signer.getAddress();
        const linkedWallets = await this.getLinkedWalletsWithDelegations(userId);
        // check membership nft first to avoid uncessary mint attempts on rejoins
        try {
            const allPromises = linkedWallets
                .map((wallet) => this.spaceDapp.hasSpaceMembership(spaceId, wallet))
                .concat(this.spaceDapp.hasSpaceMembership(spaceId, userId));
            const results = await Promise.all(allPromises);
            if (results.some((result) => result)) {
                onJoinFlowStatus?.(JoinFlowStatus.AlreadyMember);
                this.log('[joinTown] already have member nft');
                const room = await this.joinRiverSpaceAndDefaultChannels(spaceId, signerContext, onJoinFlowStatus, defaultUsername);
                return room;
            }
        }
        catch (error) {
            // skip if no membership nft found
            if (error instanceof AggregateError) {
                this.log('[joinTown] no membership nft found, proceeding with mint', error);
            }
            // otherwise some other error occurred
            else {
                addCategoryToError(error, 'river');
                throw error;
            }
        }
        try {
            this.log('[joinTown] minting membership');
            onJoinFlowStatus?.(JoinFlowStatus.MintingMembership);
            await this.baseTransactor.mintMembershipTransaction({
                spaceId,
                signer,
                xchainConfig: this.opts.xchainConfig,
            });
            this.log('[joinTown] minted membership');
            onJoinFlowStatus?.(JoinFlowStatus.MembershipMinted);
        }
        catch (error) {
            if (error &&
                typeof error === 'object' &&
                'name' in error &&
                typeof error.name === 'string' &&
                error.name.match('Membership__AlreadyMember')) {
                this.log('[joinTown] already member');
            }
            else {
                console.error('[joinTown] mint membership failed', error);
                addCategoryToError(error, 'userop');
                throw error;
            }
        }
        try {
            const room = await this.joinRiverSpaceAndDefaultChannels(spaceId, signerContext, onJoinFlowStatus, defaultUsername);
            return room;
        }
        catch (error) {
            addCategoryToError(error, 'river');
            throw error;
        }
    }
    /************************************************
     * pinMessage
     *************************************************/
    async pinMessage(roomId, eventId) {
        if (!this.casablancaClient) {
            throw new Error('Casablanca client not initialized');
        }
        await this.casablancaClient.pin(roomId, eventId);
        this.log('sendUnpin');
    }
    async unpinMessage(roomId, eventId) {
        if (!this.casablancaClient) {
            throw new Error('Casablanca client not initialized');
        }
        await this.casablancaClient.unpin(roomId, eventId);
        this.log('sendPin');
    }
    async prepayMembershipTransaction(spaceId, supply, signer) {
        return this.baseTransactor.prepayMembershipTransaction(spaceId, supply, signer);
    }
    async waitForPrepayMembershipTransaction(context) {
        const txnContext = await this.baseTransactor.waitForBlockchainTransaction(context);
        logTxnResult('waitForPrepayMembershipTransaction', txnContext);
        return txnContext;
    }
    /************************************************
     * sendMessage
     *************************************************/
    async sendMessage(roomId, message, options) {
        if (!this.casablancaClient) {
            throw new Error('Casablanca client not initialized');
        }
        switch (options?.messageType) {
            case undefined:
            case MessageType.Text:
                {
                    const mentions = options?.mentions?.map((x) => {
                        if (x.atChannel) {
                            return create(ChannelMessage_Post_MentionSchema, {
                                mentionBehavior: {
                                    case: 'atChannel',
                                    value: {},
                                },
                            });
                        }
                        else {
                            return create(ChannelMessage_Post_MentionSchema, x);
                        }
                    }) ?? [];
                    await this.casablancaClient.sendChannelMessage_Text(roomId, {
                        threadId: options?.threadId,
                        threadPreview: options?.threadPreview,
                        replyId: options?.replyId,
                        replyPreview: options?.replyPreview,
                        content: {
                            body: message,
                            mentions,
                            attachments: transformAttachments(options?.attachments),
                        },
                    }, {
                        beforeSendEventHook: options?.beforeSendEventHook,
                        onLocalEventAppended: options?.onLocalEventAppended,
                    });
                }
                break;
            case MessageType.Image:
                await this.casablancaClient.sendChannelMessage_Image(roomId, {
                    threadId: options?.threadId,
                    threadPreview: options?.threadPreview,
                    replyId: options?.replyId,
                    replyPreview: options?.replyPreview,
                    content: {
                        title: message,
                        info: options?.info,
                        thumbnail: options?.thumbnail,
                    },
                }, { beforeSendEventHook: options?.beforeSendEventHook });
                break;
            case MessageType.GM:
                await this.casablancaClient.sendChannelMessage_GM(roomId, {
                    threadId: options?.threadId,
                    threadPreview: options?.threadPreview,
                    replyId: options?.replyId,
                    replyPreview: options?.replyPreview,
                    content: {
                        typeUrl: message,
                    },
                }, { beforeSendEventHook: options?.beforeSendEventHook });
                break;
            default:
                staticAssertNever(options);
        }
        this._eventHandlers?.onSendMessage?.(roomId, message, options);
    }
    async sendMediaPayload(creationCookie, last, data, chunkIndex, iv) {
        if (!this.casablancaClient) {
            throw new Error('Casablanca client not initialized');
        }
        return await this.casablancaClient.sendMediaPayload(creationCookie, last, data, chunkIndex, iv);
    }
    /************************************************
     * sendReaction
     *************************************************/
    async sendReaction(roomId, eventId, reaction, _threadId) {
        if (!this.casablancaClient) {
            throw new Error('Casablanca client not initialized');
        }
        const beforeSendEventHooks = [];
        const beforeSendEventHook = beforeSendEventHooks.length
            ? Promise.all(beforeSendEventHooks).then(() => undefined)
            : undefined;
        await this.casablancaClient.sendChannelMessage_Reaction(roomId, {
            reaction,
            refEventId: eventId,
        }, {
            beforeSendEventHook,
        });
        this.log('sendReaction');
    }
    async sendTokenTransfer(chainId, receipt, event) {
        if (!this.casablancaClient) {
            throw new Error('Casablanca client not initialized');
        }
        await this.casablancaClient.addTransaction_Transfer(chainId, receipt, event);
    }
    /************************************************
     * retrySendMessage
     *************************************************/
    async retrySendMessage(roomId, localEventId) {
        if (!this.casablancaClient) {
            throw new Error('Casablanca client not initialized');
        }
        try {
            await this.casablancaClient.retrySendMessage(roomId, localEventId);
        }
        catch (err) {
            console.error('retrySendMessage failed', err);
        }
    }
    /************************************************
     * editMessage
     *************************************************/
    async editMessage(roomId, eventId, originalEventContent, message, options) {
        if (!this.casablancaClient) {
            throw new Error('casablanca client is undefined');
        }
        const event = this.casablancaClient.stream(roomId)?.view.events.get(eventId);
        if (!event) {
            throw new Error(`ref event not found: ${eventId}`);
        }
        if (event.remoteEvent?.creatorUserId !== this.casablancaClient.userId) {
            throw new Error(`you can only send channelMessageEdit for your own messages: ${eventId} userId: ${this.casablancaClient.userId}`);
        }
        await this.casablancaClient.sendChannelMessage_Edit_Text(roomId, eventId, {
            threadId: originalEventContent.threadId,
            threadPreview: originalEventContent.threadPreview,
            replyId: originalEventContent.replyId,
            replyPreview: originalEventContent.replyPreview,
            content: {
                body: message,
                mentions: options?.mentions?.map((x) => create(ChannelMessage_Post_MentionSchema, x)) ??
                    [],
                attachments: transformAttachments(options?.attachments),
            },
        });
    }
    /************************************************
     * redactEvent
     *************************************************/
    async redactEvent(roomId, eventId, reason) {
        if (!this.casablancaClient) {
            throw new Error('casablanca client is undefined');
        }
        const event = this.casablancaClient.stream(roomId)?.view.events.get(eventId);
        if (!event) {
            throw new Error(`ref event not found: ${eventId}`);
        }
        if (event.remoteEvent?.creatorUserId !== this.casablancaClient.userId) {
            throw new Error(`you can only send channelMessageRedaction for your own messages: ${eventId} userId: ${this.casablancaClient.userId}`);
        }
        await this.casablancaClient.sendChannelMessage_Redaction(roomId, {
            refEventId: eventId,
            reason,
        });
    }
    /************************************************
     * setRoomFullyReadData
     ************************************************/
    async setRoomFullyReadData(channelId, content) {
        if (!this.casablancaClient) {
            throw new Error('Casablanca client is undefined');
        }
        await this.casablancaClient.sendFullyReadMarkers(channelId, content);
    }
    /************************************************
     * updateUserBlock
     ************************************************/
    async updateUserBlock(userId, isBlocked) {
        if (!this.casablancaClient) {
            throw new Error('Casablanca client is undefined');
        }
        await this.casablancaClient.updateUserBlock(userId, isBlocked);
    }
    /************************************************
     * getMembership
     ************************************************/
    getMembership(streamId) {
        if (!this.casablancaClient) {
            throw new Error('casablanca client is undefined');
        }
        const userStreamId = this.casablancaClient.userStreamId;
        if (!userStreamId) {
            return Membership.None;
        }
        const membershipOp = this.casablancaClient
            .stream(userStreamId)
            ?.view.userContent.getMembership(streamId)?.op;
        return toMembership(membershipOp);
    }
    /************************************************
     * getStreamMember
     * - This function is primarily intended for testing purposes
     * **********************************************/
    getStreamMember(roomId, userId) {
        const roomData = this.getRoomData(roomId);
        return roomData?.members.find((x) => x.userId === userId);
    }
    /************************************************
     * getRoomData
     * - This function is primarily intended for testing purposes
     ************************************************/
    getRoomData(streamId) {
        if (!this.casablancaClient) {
            throw new Error('casablanca client is undefined');
        }
        const membership = this.getMembership(streamId);
        const stream = this.casablancaClient.stream(streamId);
        if (stream) {
            return toStreamView(stream, membership);
        }
        else if (membership !== Membership.None) {
            return {
                id: streamId,
                membership,
                members: [],
            };
        }
        else {
            return undefined;
        }
    }
    /************************************************
     * getIsUsernameAvailable
     ************************************************/
    getIsUsernameAvailable(streamId, username) {
        if (!this.casablancaClient) {
            throw new Error('No casablanca client');
        }
        return Promise.resolve(this.casablancaClient.isUsernameAvailable(streamId, username));
    }
    async adminRedactMessage(streamId, eventId) {
        if (!this.casablancaClient) {
            throw new Error('No casablanca client');
        }
        await this.casablancaClient.redactMessage(streamId, eventId);
    }
    async setSpaceImage(spaceStreamId, chunkedMedia) {
        if (!this.casablancaClient) {
            throw new Error('No casablanca client');
        }
        const response = await this.casablancaClient.setSpaceImage(spaceStreamId, chunkedMedia);
        return response;
    }
    async setUserProfileImage(chunkedMedia) {
        if (!this.casablancaClient) {
            throw new Error('No casablanca client');
        }
        await this.casablancaClient.setUserProfileImage(chunkedMedia);
    }
    async getUserProfileImage(userId) {
        if (!this.casablancaClient) {
            throw new Error('No casablanca client');
        }
        return this.casablancaClient.getUserProfileImage(userId);
    }
    async setUserBio(bio) {
        if (!this.casablancaClient) {
            throw new Error('No casablanca client');
        }
        await this.casablancaClient.setUserBio(bio);
    }
    /************************************************
     * setDisplayName
     ************************************************/
    // eslint-disable-next-line @typescript-eslint/require-await
    async setDisplayName(streamId, displayName) {
        if (!this.casablancaClient) {
            throw new Error('No casablanca client');
        }
        await this.casablancaClient.setDisplayName(streamId, displayName);
    }
    /************************************************
     * avatarUrl
     ************************************************/
    // eslint-disable-next-line @typescript-eslint/require-await
    async setAvatarUrl(url) {
        // todo casablanca avatar url
        console.error('not implemented for casablanca', url);
    }
    async setRoomProperties(roomId, title, topic) {
        if (!this.casablancaClient) {
            throw new Error('casablanca client is undefined');
        }
        await this.casablancaClient.updateGDMChannelProperties(roomId, title, topic);
    }
    async setChannelAutojoin(parentSpaceId, roomId, autojoin) {
        if (!this.casablancaClient) {
            throw new Error('casablanca client is undefined');
        }
        await this.casablancaClient.updateChannelAutojoin(parentSpaceId, roomId, autojoin);
    }
    async setChannelHideUserJoinLeaveEvents(parentSpaceId, roomId, hideUserJoinLeaveEvents) {
        if (!this.casablancaClient) {
            throw new Error('casablanca client is undefined');
        }
        await this.casablancaClient.updateChannelHideUserJoinLeaveEvents(parentSpaceId, roomId, hideUserJoinLeaveEvents);
    }
    // eslint-disable-next-line @typescript-eslint/require-await
    async setHighPriorityStreams(streamIds) {
        if (!this.casablancaClient) {
            throw new Error('casablanca client is undefined');
        }
        this.casablancaClient.setHighPriorityStreams(streamIds);
    }
    /************************************************
     * scrollback
     ************************************************/
    async scrollback(roomId) {
        if (!this.casablancaClient) {
            throw new Error('casablanca client is undefined');
        }
        const result = await this.casablancaClient.scrollback(roomId);
        return {
            terminus: result.terminus,
            firstEventId: result.firstEvent?.hashStr,
            fromInclusiveMiniblockNum: result.fromInclusiveMiniblockNum,
        };
    }
    async scrollbackToEvent(roomId, eventId, limit) {
        if (!this.casablancaClient) {
            throw new Error('casablanca client is undefined');
        }
        for (let i = 0; i < limit; i++) {
            await this.casablancaClient.scrollback(roomId);
            if (this.casablancaClient.stream(roomId)?.view.events.get(eventId)) {
                return true;
            }
        }
        return false;
    }
    async linkEOAToRootKey(rootKey, wallet) {
        return this.baseTransactor.linkEOAToRootKey(rootKey, wallet);
    }
    /**
     * Link the caller of the tx to the root key (link smart account)
     */
    async linkCallerToRootKey(rootKey, wallet) {
        return this.baseTransactor.linkCallerToRootKey(rootKey, wallet);
    }
    async unlinkViaRootKey(rootKey, walletAddress) {
        const result = await this.baseTransactor.unlinkViaRootKey(rootKey, walletAddress);
        this.emit('onWalletUnlinked', await rootKey.getAddress(), walletAddress);
        return result;
    }
    async unlinkViaCaller(caller) {
        return this.baseTransactor.unlinkViaCaller(caller);
    }
    async transferAsset(transferData, signer) {
        return this.baseTransactor.transferAsset(transferData, signer);
    }
    async waitForTransferAssetTransaction(transactionContext) {
        const txnContext = await this.baseTransactor.waitForBlockchainTransaction(transactionContext);
        logTxnResult('waitTransferAssetTransaction', txnContext);
        return txnContext;
    }
    tipTransaction(args) {
        return this.baseTransactor.tipTransaction(args);
    }
    async waitForTipTransaction(transactionContext) {
        const txnContext = await this.baseTransactor.waitForBlockchainTransaction(transactionContext, undefined, 
        // wait for at least 2 confirmations seems to be reliable enough for river verification
        2);
        logTxnResult('waitTipTransaction', txnContext);
        const receipt = txnContext.receipt;
        const chainId = this.spaceDapp?.config.chainId;
        if (txnContext.status === TransactionStatus.Success &&
            receipt &&
            chainId &&
            txnContext.data) {
            const tipEvent = this.spaceDapp?.getTipEvent(txnContext.data.spaceId, receipt, txnContext.data.senderAddress);
            const toUserId = txnContext.data.receiverUserId;
            if (tipEvent) {
                try {
                    await retryOperation(async () => {
                        await this.casablancaClient?.addTransaction_Tip(chainId, receipt, tipEvent, toUserId);
                    }, {
                        onError: (error, retryCount) => {
                            this.log('[waitForTipTransaction::addTransaction_Tip] error', {
                                error,
                                retryCount,
                            });
                        },
                        onRetry: (retryCount) => {
                            this.log('[waitForTipTransaction::addTransaction_Tip] retrying', {
                                retryCount,
                            });
                        },
                    });
                }
                catch (error) {
                    console.error('waitForTipTransaction:: Failed to add transaction_tip', error);
                }
            }
            else {
                console.error('waitForTipTransaction:: No tip event found');
            }
        }
        return txnContext;
    }
    async checkInTransaction(signer) {
        return this.baseTransactor.checkInTransaction(signer);
    }
    async waitForCheckInTransaction(transactionContext) {
        return this.baseTransactor.waitForBlockchainTransaction(transactionContext);
    }
    async getLinkedWallets(walletAddress) {
        return this.baseTransactor.getLinkedWallets(walletAddress);
    }
    async getLinkedWalletsWithDelegations(walletAddress) {
        return this.baseTransactor.getLinkedWalletsWithDelegations(walletAddress);
    }
    async waitWalletLinkTransaction(transactionContext) {
        const txnContext = await this.baseTransactor.waitForBlockchainTransaction(transactionContext);
        logTxnResult('waitWalletLinkTransaction', txnContext);
        return txnContext;
    }
    async getRootKeyFromLinkedWallet(walletAddress) {
        return this.baseTransactor.getRootKeyFromLinkedWallet(walletAddress);
    }
    async sendTokenTransferOperationWithCallData(args) {
        return this.baseTransactor.sendTokenTransferOperationWithCallData(args);
    }
    async waitForUserOperationWithCallDataTransaction(transactionContext) {
        return this.baseTransactor.waitForBlockchainTransaction(transactionContext, undefined, 2);
    }
    async reviewTransaction(args) {
        return this.baseTransactor.reviewTransaction(args);
    }
    async waitForReviewTransaction(context) {
        const txnContext = (await this.baseTransactor.waitForBlockchainTransaction(context, undefined, 
        // wait for at least 2 confirmations like in tip transaction
        2));
        logTxnResult('waitForReviewTransaction', txnContext);
        const receipt = txnContext.receipt;
        const chainId = this.spaceDapp?.config.chainId;
        console.log('waitForReviewTransaction receipt:', receipt);
        if (txnContext.status === TransactionStatus.Success &&
            receipt &&
            chainId &&
            txnContext.data?.spaceId) {
            console.log('waitForReviewTransaction::success', {
                status: txnContext.status,
                spaceId: txnContext.data.spaceId,
                receipt: receipt,
            });
            try {
                const reviewEvent = getSpaceReviewEventData(receipt.logs, txnContext.data.senderAddress);
                console.log('waitForReviewTransaction::reviewEvent', reviewEvent);
                if (reviewEvent) {
                    console.log('waitForReviewTransaction::success::reviewEvent', {
                        reviewEvent,
                        spaceId: txnContext.data.spaceId,
                    });
                    await retryOperation(async () => {
                        const client = this.casablancaClient;
                        await client?.addTransaction_SpaceReview(chainId, receipt, reviewEvent, txnContext.data.spaceId);
                    }, {
                        maxRetries: 3,
                        onError: (error, retryCount) => {
                            console.error('Error in addTransaction_SpaceReview:', error);
                            this.log('Retrying addTransaction_SpaceReview', { retryCount });
                        },
                        getRetryDelay: () => 1000,
                    });
                }
                else {
                    console.error('waitForReviewTransaction::error: No review event found in receipt');
                }
            }
            catch (error) {
                console.error('waitForReviewTransaction::error processing review event:', error);
            }
        }
        else {
            console.error('waitForReviewTransaction::error: Invalid transaction context', {
                status: txnContext.status,
                hasReceipt: !!receipt,
                hasChainId: !!chainId,
                hasSpaceId: !!txnContext.data?.spaceId,
            });
        }
        return txnContext;
    }
    log(message, ...optionalParams) {
        console.log(message, ...optionalParams);
    }
    debug(message, ...optionalParams) {
        console.debug(message, ...optionalParams);
    }
    setEventHandlers(eventHandlers) {
        this._eventHandlers = eventHandlers;
    }
    onLogin({ userId }) {
        this._eventHandlers?.onLogin?.({ userId: userId });
    }
}
//# sourceMappingURL=TownsClient.js.map